import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Email,
  CreditCard as CardIcon,
  CheckCircle,
  Cancel,
  Visibility,
  Delete,
  SendRounded,
  Description as CardGeneratedIcon,
  Payment as PaymentIcon,
  Send as FederationIcon,
} from '@mui/icons-material';
import { Switch, FormControlLabel, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Card } from '../../types';
import api from '../../services/api';
import PilotDetailsModal from './PilotDetailsModal';
import CardViewModal from './CardViewModal';

interface CardsTableProps {
  cards: Card[];
  onRefresh: () => Promise<void>;
  showClubColumn?: boolean;
  // Optional: external pagination control
  page?: number;
  rowsPerPage?: number;
  totalCount?: number; // For server-side pagination - total number of cards
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  // Optional: hide internal alerts (parent handles them)
  hideAlerts?: boolean;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  // Optional: club-specific settings for FIVL button
  nationalFederationEmail?: string;
  // Optional: loading states from parent
  generatingCard?: { [cardId: string]: boolean };
  sendingEmail?: { [cardId: string]: boolean };
}

const CardsTable: React.FC<CardsTableProps> = ({
  cards,
  onRefresh,
  showClubColumn = false,
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  hideAlerts = false,
  onError,
  onSuccess,
  nationalFederationEmail,
  generatingCard: externalGeneratingCard,
  sendingEmail: externalSendingEmail,
}) => {
  const { t } = useTranslation();

  // Internal pagination state (used when no external control)
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(25);

  // Use external or internal pagination
  const page = externalPage ?? internalPage;
  const rowsPerPage = externalRowsPerPage ?? internalRowsPerPage;

  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCardId, setViewCardId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<{ [key: string]: boolean }>({});

  // Internal loading states (used when no external control)
  const [internalGeneratingCard, setInternalGeneratingCard] = useState<{ [cardId: string]: boolean }>({});
  const [internalSendingEmail, setInternalSendingEmail] = useState<{ [cardId: string]: boolean }>({});

  // Use external or internal loading states
  const generatingCard = externalGeneratingCard ?? internalGeneratingCard;
  const sendingEmail = externalSendingEmail ?? internalSendingEmail;

  // Update selectedCard when cards array changes (e.g., after refresh)
  useEffect(() => {
    setSelectedCard(prevSelected => {
      if (prevSelected) {
        const updatedCard = cards.find(c => c.record_id === prevSelected.record_id);
        if (updatedCard) {
          return updatedCard;
        }
      }
      return prevSelected;
    });
  }, [cards]);

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
  };

  const reportError = (message: string) => {
    if (onError) {
      onError(message);
    } else {
      setError(message);
    }
  };

  const reportSuccess = (message: string) => {
    if (onSuccess) {
      onSuccess(message);
    } else {
      setSuccess(message);
    }
  };

  const handleRowClick = (card: Card) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCard(null);
  };

  const handleViewCard = (recordId: string) => {
    setViewCardId(recordId);
    setViewModalOpen(true);
  };

  const handleGenerateCard = async (cardId: string, isRegenerate: boolean = false) => {
    if (!externalGeneratingCard) {
      setInternalGeneratingCard(prev => ({ ...prev, [cardId]: true }));
    }
    try {
      const endpoint = isRegenerate
        ? `/regenerate-membership-card/${cardId}`
        : `/generate-one-membership-card/${cardId}`;

      await api.post(endpoint);
      await onRefresh();
      reportSuccess(isRegenerate ? 'Card regenerated successfully!' : 'Card generated successfully!');
    } catch (error: any) {
      console.error('Failed to generate card:', error);
      let errorMessage = isRegenerate ? 'Failed to regenerate card' : 'Failed to generate card';

      if (error.response?.status === 500) {
        errorMessage = error.response?.data?.detail || 'Server error: The club may not be configured properly. Please check club settings.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      reportError(errorMessage);
    } finally {
      if (!externalGeneratingCard) {
        setInternalGeneratingCard(prev => ({ ...prev, [cardId]: false }));
      }
    }
  };

  const handleSendEmail = async (cardId: string, isResend: boolean = false) => {
    if (!externalSendingEmail) {
      setInternalSendingEmail(prev => ({ ...prev, [cardId]: true }));
    }
    try {
      const endpoint = isResend
        ? `/resend-email/${cardId}`
        : `/send-email-by-id/${cardId}`;

      await api.post(endpoint);
      await onRefresh();
      reportSuccess(isResend ? 'Email resent successfully!' : 'Email sent successfully!');
    } catch (error: any) {
      console.error('Failed to send email:', error);
      let errorMessage = isResend ? 'Failed to resend email' : 'Failed to send email';

      if (error.response?.status === 500) {
        errorMessage = error.response?.data?.detail || 'Server error: Failed to send email. Please check email configuration.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      reportError(errorMessage);
    } finally {
      if (!externalSendingEmail) {
        setInternalSendingEmail(prev => ({ ...prev, [cardId]: false }));
      }
    }
  };

  const handleToggleActive = async (cardId: string) => {
    try {
      await api.post(`/toggle-card-activation/${cardId}`);
      await onRefresh();
    } catch (error) {
      console.error('Failed to toggle card status:', error);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!window.confirm('Are you sure you want to delete this card?')) {
      return;
    }
    try {
      await api.delete(`/cards/${cardId}`);
      await onRefresh();
      reportSuccess('Card deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete card:', error);
      reportError(error.response?.data?.detail || 'Failed to delete card');
    }
  };

  const handleSendFivlEmail = async (cardId: string) => {
    if (!window.confirm('Sei sicuro di voler procedere?')) {
      return;
    }
    try {
      await api.post(`/send-fivl-email/${cardId}`);
      await onRefresh();
      reportSuccess('Operazione Eseguita con Successo');
    } catch (error: any) {
      console.error('Failed to send FIVL email:', error);
      reportError(error.response?.data?.detail || 'Errore nella operazione');
    }
  };

  const handlePaymentToggle = async (card: Card) => {
    const cardId = card.record_id;
    try {
      setPaymentLoading(prev => ({ ...prev, [cardId]: true }));

      await api.post(`/update-pagato/${cardId}`, {
        pagato: !card.pagato
      });

      await onRefresh();
      reportSuccess('Payment status updated successfully');
    } catch (err: any) {
      console.error('Failed to update payment status:', err);
      reportError('Failed to update payment status');
    } finally {
      setPaymentLoading(prev => ({ ...prev, [cardId]: false }));
    }
  };

  // Check if FIVL button should be shown for a card
  const shouldShowFivlButton = (card: Card) => {
    // If nationalFederationEmail prop is provided, use it
    // Otherwise fall back to checking card.fivl (for backward compatibility)
    const hasFederationEmail = nationalFederationEmail !== undefined
      ? !!nationalFederationEmail
      : card.fivl;

    return hasFederationEmail && card.adesione_file_url && card.pagamento_file_url;
  };

  // If totalCount is provided (server-side pagination), cards are already the current page
  // Otherwise, slice cards for client-side pagination
  const displayCards = totalCount !== undefined ? cards : cards.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // For pagination count: use totalCount if provided, otherwise use cards.length
  const paginationCount = totalCount ?? cards.length;

  return (
    <>
      {!hideAlerts && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!hideAlerts && success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: { xs: 600, md: 800 } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('cards.table.id')}</TableCell>
              <TableCell>{t('cards.table.name')}</TableCell>
              <TableCell>{t('cards.table.email')}</TableCell>
              {showClubColumn && <TableCell>Club</TableCell>}
              <TableCell>{t('cards.table.status')}</TableCell>
              <TableCell>{t('cards.table.payment')}</TableCell>
              <TableCell align="center">{t('cards.table.timeline')}</TableCell>
              <TableCell align="center">{t('cards.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayCards.map((card) => (
              <TableRow
                key={card.record_id}
                hover
                onClick={() => handleRowClick(card)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: !card.active ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                  '&:hover': {
                    backgroundColor: !card.active ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <TableCell>{card.id}</TableCell>
                <TableCell>{`${card.nome || 'N/A'} ${card.cognome || ''}`}</TableCell>
                <TableCell>{card.email}</TableCell>
                {showClubColumn && (
                  <TableCell>
                    {card.club ? (
                      <Box>
                        <Typography variant="body2">{card.club.club_name}</Typography>
                        {card.club.is_visitor_club && (
                          <Chip
                            label="Visitor"
                            size="small"
                            sx={{
                              backgroundColor: '#ff00fb',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20,
                              mt: 0.5,
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={card.active ? t('cards.status.active') : t('cards.status.inactive')}
                    color={card.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={card.pagato || false}
                        onChange={() => {
                          handlePaymentToggle(card);
                        }}
                        disabled={paymentLoading[card.record_id]}
                        color="success"
                        size="small"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                  {paymentLoading[card.record_id] && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip
                      title={card.card_generated_at
                        ? `${t('cards.timeline.cardGenerated')}: ${format(new Date(card.card_generated_at), 'dd/MM/yyyy HH:mm')}`
                        : `${t('cards.timeline.cardGenerated')}: ${t('cards.timeline.pending')}`
                      }
                    >
                      <CardGeneratedIcon
                        fontSize="small"
                        sx={{
                          color: card.card_generated_at ? 'success.main' : 'grey.400',
                          cursor: 'help'
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      title={card.email_sent_at
                        ? `${t('cards.timeline.emailSent')}: ${format(new Date(card.email_sent_at), 'dd/MM/yyyy HH:mm')}`
                        : `${t('cards.timeline.emailSent')}: ${t('cards.timeline.pending')}`
                      }
                    >
                      <Email
                        fontSize="small"
                        sx={{
                          color: card.email_sent_at ? 'success.main' : 'grey.400',
                          cursor: 'help'
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      title={card.pagato_at
                        ? `${t('cards.timeline.payment')}: ${format(new Date(card.pagato_at), 'dd/MM/yyyy HH:mm')}`
                        : `${t('cards.timeline.payment')}: ${t('cards.timeline.pending')}`
                      }
                    >
                      <PaymentIcon
                        fontSize="small"
                        sx={{
                          color: card.pagato_at ? 'success.main' : 'grey.400',
                          cursor: 'help'
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      title={card.email_fivl_sent_at
                        ? `${t('cards.timeline.federationSent')}: ${format(new Date(card.email_fivl_sent_at), 'dd/MM/yyyy HH:mm')}`
                        : `${t('cards.timeline.federationSent')}: ${t('cards.timeline.pending')}`
                      }
                    >
                      <FederationIcon
                        fontSize="small"
                        sx={{
                          color: card.email_fivl_sent_at ? 'success.main' : 'grey.400',
                          cursor: 'help'
                        }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {card.card_generated_at && (
                      <Tooltip title={t('cards.actions.viewCard')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCard(card.record_id);
                          }}
                          color="warning"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={card.card_generated_at ? "Regenerate Card" : "Generate Card"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateCard(card.record_id, !!card.card_generated_at);
                          }}
                          disabled={generatingCard[card.record_id]}
                        >
                          {generatingCard[card.record_id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <CardIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={card.email_sent_at ? t('cards.actions.resendEmail', 'Resend Email') : t('cards.actions.sendEmail')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendEmail(card.record_id, !!card.email_sent_at);
                          }}
                          disabled={!card.card_generated_at || sendingEmail[card.record_id]}
                        >
                          {sendingEmail[card.record_id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Email fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('cards.actions.toggleActive')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActive(card.record_id);
                        }}
                      >
                        {card.active ? (
                          <Cancel fontSize="small" color="error" />
                        ) : (
                          <CheckCircle fontSize="small" color="success" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {shouldShowFivlButton(card) && (
                      <Tooltip title="Send to National Federation">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendFivlEmail(card.record_id);
                          }}
                          color="info"
                        >
                          <SendRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('cards.actions.deleteCard')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card.record_id);
                        }}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={paginationCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => {
            handleRowsPerPageChange(parseInt(e.target.value, 10));
          }}
        />
      </TableContainer>

      <PilotDetailsModal
        open={modalOpen}
        onClose={handleModalClose}
        card={selectedCard}
        onRefresh={onRefresh}
      />

      <CardViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewCardId(null);
        }}
        cardId={viewCardId}
      />
    </>
  );
};

export default CardsTable;
